/**
 * config/oauth-providers.ts
 * OAuth2 provider configurations for cloud sync
 *
 * Centralizes OAuth configuration for all cloud-based sync providers.
 * Each function returns an OAuthConfig with provider-specific endpoints,
 * scopes, and client credentials loaded from environment variables.
 *
 * The redirect URI is dynamically determined from window.location.origin,
 * allowing the same configuration to work across dev/beta/production environments.
 */

import { Environment } from '@/config/environment';
import type { OAuthConfig } from '@/types/sync/OAuthConfig';

/**
 * Get Google Drive OAuth2 configuration
 *
 * Returns OAuth config for Google Drive API access with the following:
 * - Scope: `drive.file` (access only to files created by this app)
 * - Offline access: Gets refresh token for long-term access
 * - PKCE flow: Secure for Single Page Applications
 *
 * **Setup Required**:
 * 1. Create OAuth2 client ID in Google Cloud Console
 * 2. Enable Google Drive API
 * 3. Add authorized redirect URIs for each environment
 * 4. Set VITE_GOOGLE_DRIVE_CLIENT_ID in .env.local
 *
 * @returns Google Drive OAuth2 configuration
 * @throws Error if GOOGLE_DRIVE_CLIENT_ID is not configured
 *
 * @example
 * ```typescript
 * const config = getGoogleDriveConfig();
 * // config.clientId = 'your-client-id.apps.googleusercontent.com'
 * // config.redirectUri = 'https://dev.enfomentis.app/oauth/callback'
 * ```
 */
export function getGoogleDriveConfig(): OAuthConfig {
  const clientId = Environment.GOOGLE_DRIVE_CLIENT_ID;

  if (!clientId) {
    throw new Error(
      'Google Drive client ID not configured. ' +
        'Please set VITE_GOOGLE_DRIVE_CLIENT_ID in your .env.local file. ' +
        'See .env.example for setup instructions.'
    );
  }

  // NOTE: With BFF (Backend-for-Frontend) pattern, OAuth is handled server-side
  // via Supabase Edge Functions. The config below is kept for compatibility with
  // OAuthCloudSyncProvider base class, but auth/token endpoints are not used directly.
  //
  // OAuth flow: Browser → google-oauth-start → Google → google-oauth-callback → Browser
  // Token storage: Supabase database (encrypted)
  // API calls: Browser → google-drive-proxy → Google Drive API

  return {
    provider: 'google-drive',
    clientId,
    authEndpoint: '', // Not used (handled by Edge Function)
    tokenEndpoint: '', // Not used (handled by Edge Function)
    scopes: ['https://www.googleapis.com/auth/drive.file'],
    redirectUri: `${window.location.origin}/settings/data-sync`,
    // No additionalAuthParams or additionalTokenParams needed
    // All OAuth configuration is server-side in Edge Functions
  };
}

/**
 * Get OneDrive OAuth2 configuration
 *
 * @returns OneDrive OAuth2 configuration
 * @throws Error - OneDrive provider not yet implemented
 */
export function getOneDriveConfig(): OAuthConfig {
  throw new Error(
    'OneDrive provider not yet implemented. ' +
      'This will be available in Sprint 2 (OneDrive & Dropbox Implementation).'
  );
}

/**
 * Get Dropbox OAuth2 configuration
 *
 * @returns Dropbox OAuth2 configuration
 * @throws Error - Dropbox provider not yet implemented
 */
export function getDropboxConfig(): OAuthConfig {
  throw new Error(
    'Dropbox provider not yet implemented. ' +
      'This will be available in Sprint 2 (OneDrive & Dropbox Implementation).'
  );
}

/**
 * Get Box OAuth2 configuration
 *
 * @returns Box OAuth2 configuration
 * @throws Error - Box provider not yet implemented
 */
export function getBoxConfig(): OAuthConfig {
  throw new Error(
    'Box provider not yet implemented. ' +
      'This will be available in Sprint 3 (Box Implementation & Multi-Provider UI).'
  );
}
